// src/utils/webMatchProvenance.ts
//
// How a web-discovered match is badged with the pass that found it.
//
// A pure module with no imports, because this repo tests logic rather than
// rendering — there is no React Testing Library here and every existing suite is
// a `.test.ts` over a plain function. Keeping the decision out of the component
// is what makes it assertable at all.
//
// ---- What the badge is for ---------------------------------------------------
//
// The photo flow runs two independent web passes. The gpt-4o text search looks
// for the WORDS read off the photograph; the reverse image search asks what the
// OBJECT looks like. They share no inputs and fail in different places, which is
// the reason for running both — and a row BOTH passes landed on is the strongest
// signal the flow can produce, two methods with nothing in common agreeing.
//
// Before the backend sent this, an operator could only infer the branch from
// whether a THUMBNAIL was present: image-pass findings carry one, text-pass
// findings do not. That proxy is silent about exactly the rows worth knowing
// about, because a corroborated row looks identical to an image-only one.

/** Which web pass produced a match. Mirrors watchlock's `WebMatch`. */
export type WebMatchProvenance = 'text' | 'image' | 'both';

export interface ProvenanceBadge {
  label: string;
  /** Tailwind background + text colour. */
  className: string;
  /** Hover explanation — the badge itself is one word. */
  title: string;
}

const BADGES: Record<WebMatchProvenance, ProvenanceBadge> = {
  text: {
    label: 'text',
    className: 'bg-slate-100 text-slate-700',
    title: 'Found by the text web search only',
  },
  image: {
    label: 'image',
    className: 'bg-indigo-100 text-indigo-800',
    title: 'Found by the reverse image search only',
  },
  both: {
    label: 'both',
    // The only positive colour in the set. `text` and `image` name a category;
    // `both` reports corroboration, which is a different kind of claim and
    // should not read as just a third category.
    className: 'bg-emerald-100 text-emerald-800',
    title: 'Found by BOTH passes independently — the strongest signal this flow produces',
  },
};

/**
 * Badge for a provenance value, or `null` when there is nothing to show.
 *
 * `null` covers three cases deliberately treated the same: the field is absent
 * (every response predating it), it is empty, or it holds a value this build
 * does not know. `discoveredVia` is unvalidated backend JSON — the shape is
 * owned by watchlock, not shared-types — so an unrecognised value must render
 * nothing rather than an unstyled or mislabelled badge.
 */
export function webMatchProvenanceBadge(via: string | undefined | null): ProvenanceBadge | null {
  if (!via) return null;
  return BADGES[via as WebMatchProvenance] ?? null;
}
